import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Loader2, Search, Rocket } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { OnboardingPlan, OnboardingTemplate } from '../../../api/onboardingApi';
import * as api from '../../../api/onboardingApi';
import { apiClient } from '@/services/apiClient';
import PlanDetailPage from './PlanDetailPage';

interface OrgOption { id: number; name: string; customer_status?: string; }
interface UserOption { id: number; name: string; email?: string; role?: string; }

const healthBadge: Record<string, string> = {
  green: 'bg-green-100 text-green-700', yellow: 'bg-yellow-100 text-yellow-700', red: 'bg-red-100 text-red-700',
};

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  draft: { label: 'Draft', variant: 'outline' },
  active: { label: 'Active', variant: 'default' },
  on_hold: { label: 'On Hold', variant: 'secondary' },
  completed: { label: 'Completed', variant: 'default' },
  churned: { label: 'Churned', variant: 'destructive' },
};

export default function PlansList() {
  const { t } = useAppTranslation();
  const [plans, setPlans] = useState<OnboardingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterHealth, setFilterHealth] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [templates, setTemplates] = useState<OnboardingTemplate[]>([]);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [orgSearch, setOrgSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [createForm, setCreateForm] = useState({ customer_org_id: '', template_id: '', name: '', target_go_live_date: '', csm_user_id: '' });

  const fetchPlans = useCallback(async () => {
    try {
      const params: Record<string, string | number> = {};
      if (filterStatus) params.status = filterStatus;
      if (filterHealth) params.health = filterHealth;
      setPlans(await api.listPlans(params));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterStatus, filterHealth]);

  useEffect(() => { setLoading(true); fetchPlans(); }, [fetchPlans]);

  const handleCreateOpen = async () => {
    setShowCreate(true);
    setOrgSearch('');
    setUserSearch('');
    try {
      const [tmpls, orgsRes, usersRes] = await Promise.all([
        api.listTemplates({ is_active: true }),
        apiClient.get<OrgOption[]>('/api/cbos'),
        apiClient.get<UserOption[]>('/api/users/all-staff'),
      ]);
      setTemplates(tmpls);
      setOrgs(orgsRes.data);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data as any)?.users || []);
    } catch (err) { console.error(err); }
  };

  const filteredOrgs = orgs.filter(o => o.name.toLowerCase().includes(orgSearch.toLowerCase()));
  const filteredUsers = users.filter(u => {
    const q = userSearch.toLowerCase();
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const handleCreate = async () => {
    if (!createForm.customer_org_id || !createForm.template_id) return;
    setCreating(true);
    try {
      const res = await api.createPlan({
        customer_org_id: parseInt(createForm.customer_org_id),
        template_id: parseInt(createForm.template_id),
        name: createForm.name || undefined,
        target_go_live_date: createForm.target_go_live_date || undefined,
        csm_user_id: createForm.csm_user_id ? parseInt(createForm.csm_user_id) : undefined,
      });
      setShowCreate(false);
      setCreateForm({ customer_org_id: '', template_id: '', name: '', target_go_live_date: '', csm_user_id: '' });
      fetchPlans();
      setSelectedPlanId(res.id);
    } catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    finally { setCreating(false); }
  };

  const filtered = plans.filter(p => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (p.customer_org_name || '').toLowerCase().includes(q) || (p.template_name || '').toLowerCase().includes(q) || (p.csm_user_name || '').toLowerCase().includes(q);
  });

  // Full-page plan detail view
  if (selectedPlanId) {
    return <PlanDetailPage planId={selectedPlanId} onBack={() => setSelectedPlanId(null)} onUpdated={fetchPlans} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">{t('onboarding.plans.title', 'Onboarding Plans')}</h2>
          <p className="text-sm text-gray-500">Track customer onboarding progress</p>
        </div>
        <Button onClick={handleCreateOpen}><Plus className="h-4 w-4 mr-1" />New Plan</Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input className="pl-9" placeholder="Search customer, template, CSM..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="">All statuses</SelectItem>
            {Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterHealth} onValueChange={setFilterHealth}>
          <SelectTrigger className="w-32"><SelectValue placeholder="All health" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="">All health</SelectItem>
            <SelectItem value="green">Green</SelectItem>
            <SelectItem value="yellow">Yellow</SelectItem>
            <SelectItem value="red">Red</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Plans table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-400">
          {plans.length === 0 ? (
            <div className="space-y-2"><Rocket className="h-10 w-10 mx-auto text-gray-300" /><p>No onboarding plans yet</p><Button variant="outline" onClick={handleCreateOpen}>Create your first plan</Button></div>
          ) : 'No plans match your filters'}
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50/80">
                    <th className="text-left py-2.5 px-4 font-medium text-gray-500">Customer</th>
                    <th className="text-left py-2.5 px-3 font-medium text-gray-500">Template</th>
                    <th className="text-left py-2.5 px-3 font-medium text-gray-500">Phase</th>
                    <th className="text-center py-2.5 px-3 font-medium text-gray-500">Progress</th>
                    <th className="text-center py-2.5 px-3 font-medium text-gray-500">Health</th>
                    <th className="text-left py-2.5 px-3 font-medium text-gray-500">CSM</th>
                    <th className="text-left py-2.5 px-3 font-medium text-gray-500">Go-Live</th>
                    <th className="text-center py-2.5 px-3 font-medium text-gray-500">Issues</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map((plan) => {
                    const total = plan.task_total || 0;
                    const done = plan.task_done || 0;
                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                    const sc = statusConfig[plan.status] || statusConfig.draft;
                    const blocked = plan.task_blocked || 0;
                    const overdue = plan.task_overdue || 0;
                    return (
                      <tr key={plan.id} onClick={() => setSelectedPlanId(plan.id)} className="cursor-pointer hover:bg-gray-50 transition-colors">
                        <td className="py-2.5 px-4">
                          <p className="font-medium">{plan.customer_org_name || `Org #${plan.customer_org_id}`}</p>
                          <Badge variant={sc.variant} className="text-xs mt-0.5">{sc.label}</Badge>
                        </td>
                        <td className="py-2.5 px-3 text-gray-600">{plan.template_name || '—'}</td>
                        <td className="py-2.5 px-3 text-gray-600">{plan.current_phase_name || '—'}</td>
                        <td className="py-2.5 px-3">
                          <div className="flex flex-col items-center gap-1">
                            <div className="w-16 bg-gray-100 rounded-full h-1.5">
                              <div className={`h-1.5 rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-gray-500">{pct}%</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <Badge variant="outline" className={`text-xs capitalize ${healthBadge[plan.health] || ''}`}>{plan.health}</Badge>
                        </td>
                        <td className="py-2.5 px-3 text-gray-600">{plan.csm_user_name || '—'}</td>
                        <td className="py-2.5 px-3 text-gray-500 text-xs">
                          {plan.target_go_live_date ? new Date(plan.target_go_live_date).toLocaleDateString() : '—'}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          {(blocked > 0 || overdue > 0) ? (
                            <div className="flex justify-center gap-1">
                              {blocked > 0 && <Badge variant="destructive" className="text-xs">{blocked} blocked</Badge>}
                              {overdue > 0 && <Badge variant="secondary" className="text-xs">{overdue} overdue</Badge>}
                            </div>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create plan dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Customer Onboarding</DialogTitle>
            <DialogDescription>Select a template and customer to create a new onboarding plan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Customer Organization</Label>
              <Select value={createForm.customer_org_id} onValueChange={(v) => setCreateForm({ ...createForm, customer_org_id: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select an organization..." /></SelectTrigger>
                <SelectContent>
                  <div className="px-2 pb-1.5">
                    <Input placeholder="Search organizations..." value={orgSearch} onChange={(e) => { e.stopPropagation(); setOrgSearch(e.target.value); }} className="h-8" />
                  </div>
                  {filteredOrgs.length === 0 ? (
                    <div className="py-2 px-3 text-sm text-gray-400">No organizations found</div>
                  ) : filteredOrgs.map(o => (
                    <SelectItem key={o.id} value={String(o.id)}>
                      {o.name}{o.customer_status ? ` (${o.customer_status})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Template</Label>
              <Select value={createForm.template_id} onValueChange={(v) => setCreateForm({ ...createForm, template_id: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select a template..." /></SelectTrigger>
                <SelectContent>
                  {templates.map(tmpl => <SelectItem key={tmpl.id} value={String(tmpl.id)}>{tmpl.name}{tmpl.is_platform_default ? ' (Platform)' : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>CSM (Customer Success Manager)</Label>
              <Select value={createForm.csm_user_id} onValueChange={(v) => setCreateForm({ ...createForm, csm_user_id: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Assign a CSM..." /></SelectTrigger>
                <SelectContent>
                  <div className="px-2 pb-1.5">
                    <Input placeholder="Search users..." value={userSearch} onChange={(e) => { e.stopPropagation(); setUserSearch(e.target.value); }} className="h-8" />
                  </div>
                  <SelectItem value="">Unassigned</SelectItem>
                  {filteredUsers.map(u => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.name || u.email}{u.role ? ` (${u.role})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plan Name (optional)</Label>
              <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="mt-1" placeholder="e.g., Acme Corp Onboarding" />
            </div>
            <div>
              <Label>Target Go-Live Date</Label>
              <Input type="date" value={createForm.target_go_live_date} onChange={(e) => setCreateForm({ ...createForm, target_go_live_date: e.target.value })} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !createForm.customer_org_id || !createForm.template_id}>
              {creating ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Creating...</> : 'Start Onboarding'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
